import { IntlShape } from 'react-intl';
import { Locale } from '@synergy/models';
import { getLocalizedString } from '@synergy/utils';
import dayjs from 'dayjs';

import { Account } from '../../../common/models/account';
import { Contact } from '../../../common/models/contact';
import { PaymentTerm } from '../../../common/models/payment-term';
import { PoolTimelineItemChange } from '../../../common/components/timeline/models/pool-timeline-itemChange';
import { TaxRate } from '../../../common/models/tax-rate';

/**
 * Turns a change set into a sentence a reader understands (#2154).
 *
 * The stream used to head every edit with "Bestellung bearbeitet" and fold the
 * fields away behind "Details anzeigen". The study asks for the opposite: say
 * what happened, in a whole sentence, without a click. Everything needed for
 * that is already in the log — this only brings it forward.
 *
 * What this cannot do is state a consequence ("weicht vom Bestellpreis ab").
 * The log knows that a price went from 100 to 107.20, not what it should have
 * been. Those sentences need the backend to recognise the situation and record
 * it as an event of its own; they are deliberately out of scope here.
 */

/** How many fields the headline names before it starts counting. */
const NAMED_FIELDS = 2;

/** A value long enough to push the sentence off the row is cut here. */
const VALUE_LENGTH = 48;

/** Fields whose numbers are amounts, so they read with two decimals. */
const AMOUNT_FIELDS = [
    'netShippingCosts',
    'netSurcharge',
    'price',
    'shippingCosts',
    'surcharge',
    'totalNetPrice',
    'totalPrice',
    'totalRecurringNetPrice',
    'totalRecurringPrice',
];

/** Fields holding a reference to an account, by the id the log keeps. */
const ACCOUNT_FIELDS = ['account', 'customerAccount', 'supplierAccount'];

/** The same for contacts. */
const CONTACT_FIELDS = [
    'avisoContact',
    'contact',
    'customerContact',
    'defaultContact',
    'mainContact',
    'responsibleContact',
    'supplierContact',
];

/** Fields pointing at a payment term, which carries its own wording. */
const PAYMENT_TERM_FIELDS = ['paymentTerm', 'poolPaymentTerm'];

/** Fields pointing at a tax rate — the "Sach/Steuerkonto" of a member. */
const TAX_RATE_FIELDS = ['bonusTaxRate', 'taxRate'];

/**
 * A value that is a key, not a word: XML_STORE_FTP_BOTH, CREDIT, PAYMENT_METHOD_DEBIT.
 *
 * The log stores what the enum stores. Which of them appear is not a question
 * of taste: measured over the account log of the last year, three fields carry
 * them — the billing modality, the payment method and the SFTP option.
 */
const ENUM_TOKEN = /^[A-Z][A-Z0-9_]*$/;

/**
 * The names the log does not carry.
 *
 * Every entity in a change set is reduced to its id when it is written
 * (LoggableListener::transformValue), so a name can only come from what the
 * client already holds. Both lists are in the store for the pickers anyway —
 * this asks no endpoint of its own.
 */
export interface ChangeVocabulary {
    accounts: Account[];
    contacts: Contact[];
    /** Both carry a localized text; without them the sentence says "#171". */
    paymentTerms: PaymentTerm[];
    taxRates: TaxRate[];
    locales: Locale[];
    selectedLocale: Locale;
}

/**
 * Whatever comes out of the log, as text.
 *
 * The change set is JSON written by a serializer over fifteen years of entity
 * shapes; a value that is not a string is not an exception here. Coercing at
 * this one point is what keeps an unexpected shape from reaching React, which
 * refuses to render an object and takes the whole mask down with it.
 */
const shorten = (value: unknown): string => {
    const text = 'string' === typeof value ? value : String(value ?? '');

    return text.length > VALUE_LENGTH ? text.slice(0, VALUE_LENGTH - 1).trimEnd() + '…' : text;
};

const isDateString = (value: string): boolean => /^\d{4}-\d{2}-\d{2}[T ]/.test(value);

const formatDate = (value: dayjs.Dayjs): string =>
    // Midnight is how a date without a time is stored; printing "00:00" would
    // claim a precision the field never had.
    0 === value.hour() && 0 === value.minute() ? value.format('DD.MM.YYYY') : value.format('DD.MM.YYYY HH:mm');

const referenceName = (
    id: number,
    propertyName: string,
    vocabulary: ChangeVocabulary,
    intl: IntlShape
): string | undefined => {
    if (ACCOUNT_FIELDS.includes(propertyName)) {
        const account = vocabulary.accounts.find(entry => entry.id === id);

        return account?.name ?? intl.formatMessage({ id: 'app.stream.sentence.gone' }, { id });
    }

    if (CONTACT_FIELDS.includes(propertyName)) {
        const contact = vocabulary.contacts.find(entry => entry.id === id);

        return contact?.fullName ?? intl.formatMessage({ id: 'app.stream.sentence.gone' }, { id });
    }

    if (PAYMENT_TERM_FIELDS.includes(propertyName)) {
        const term = vocabulary.paymentTerms.find(entry => entry.id === id);
        const text = term ? getLocalizedString(vocabulary.locales, term.text) : undefined;

        return text && '' !== text ? text : `#${id}`;
    }

    if (TAX_RATE_FIELDS.includes(propertyName)) {
        const rate = vocabulary.taxRates.find(entry => entry.id === id);
        const text = rate ? getLocalizedString(vocabulary.locales, rate.description) : undefined;

        return text && '' !== text ? text : `#${id}`;
    }

    // Anything else the log points at — an address, a settlement — has no list
    // in the client. The id at least identifies it; inventing a label would not.
    return `#${id}`;
};

/**
 * One value, written the way the field means it.
 *
 * Shape decides before the field name does: the log stores dates as ATOM
 * strings today and as {date, timezone} in older rows, and both have to read
 * the same.
 */
export const formatChangeValue = (
    value: unknown,
    propertyName: string,
    vocabulary: ChangeVocabulary,
    intl: IntlShape
): string => {
    if (null === value || undefined === value || '' === value) {
        return intl.formatMessage({ id: 'app.stream.sentence.empty' });
    }

    if ('boolean' === typeof value) {
        return intl.formatMessage({ id: `app.activity.changes.${value ? 'true' : 'false'}` });
    }

    if ('number' === typeof value) {
        return AMOUNT_FIELDS.includes(propertyName)
            ? intl.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : intl.formatNumber(value);
    }

    if ('string' === typeof value) {
        if (isDateString(value)) {
            return formatDate(dayjs(value));
        }

        // Translated fields arrive as the JSON of their values; the reader wants
        // the one language they are working in. Value objects arrive the same
        // way and have no reading — their raw JSON in a sentence helps nobody.
        if (value.startsWith('{"')) {
            try {
                const parsed = JSON.parse(value);
                const localized = getLocalizedString(vocabulary.locales, parsed);

                return 'string' === typeof localized && '' !== localized
                    ? shorten(localized)
                    : intl.formatMessage({ id: 'app.stream.sentence.unreadable' });
            } catch {
                return shorten(value);
            }
        }

        if (AMOUNT_FIELDS.includes(propertyName) && '' !== value && !isNaN(Number(value))) {
            return intl.formatNumber(Number(value), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        // Asked only where there is an answer: a VAT number and a supplier
        // called "TEST" read as enum keys too, and formatMessage would log a
        // missing translation for each of them.
        if (ENUM_TOKEN.test(value)) {
            const key = `app.enum.${value}`;

            if (intl.messages[key]) {
                return intl.formatMessage({ id: key });
            }
        }

        return shorten(value);
    }

    if ('object' === typeof value) {
        const object = value as Record<string, unknown>;

        // Three shapes of the same thing sit in the log side by side, written by
        // as many serializers over the years. Only the first two carry the date
        // as text; the third is a normalised DateTimeImmutable, and its unix
        // seconds are the one field of it worth reading.
        if ('string' === typeof object['date']) {
            const zone = 'string' === typeof object['timezone'] ? object['timezone'] : '';

            return formatDate(dayjs(object['date'] + zone));
        }

        if ('number' === typeof object['timestamp']) {
            return formatDate(dayjs.unix(object['timestamp']));
        }

        // A value object carries its own label, and that reads far better than
        // the id it happens to have: a payment term is "70% Anzahlung …" to
        // everyone except the database.
        const own = object['text'];

        if (null !== own && 'object' === typeof own) {
            const ownText = getLocalizedString(vocabulary.locales, own as Partial<Record<Locale, string>>);

            if ('string' === typeof ownText && '' !== ownText) {
                return shorten(ownText);
            }
        }

        if ('number' === typeof object['id']) {
            return shorten(referenceName(object['id'], propertyName, vocabulary, intl) ?? `#${object['id']}`);
        }

        // A translated field that was not stringified on its way here.
        const localized = getLocalizedString(vocabulary.locales, object);

        if ('string' === typeof localized && '' !== localized) {
            return shorten(localized);
        }

        // Nothing recognised it. Saying so beats printing the serializer's
        // internals into a sentence someone is meant to read.
        return intl.formatMessage({ id: 'app.stream.sentence.unreadable' });
    }

    return shorten(value);
};

const fieldLabel = (propertyName: string, intl: IntlShape): string =>
    intl.formatMessage({ id: 'app.activity.changes.field.' + propertyName, defaultMessage: propertyName });

/**
 * "Lieferdatum von 10.09.2020 auf 24.09.2020 geändert", in the shape the values
 * allow. Each of these is a whole clause with its own verb, so the sentence
 * around them only has to join them.
 */
const changePart = (change: PoolTimelineItemChange, vocabulary: ChangeVocabulary, intl: IntlShape): string => {
    const field = fieldLabel(change.propertyName, intl);
    const empty = intl.formatMessage({ id: 'app.stream.sentence.empty' });
    const from = formatChangeValue(change.oldValue, change.propertyName, vocabulary, intl);
    const to = formatChangeValue(change.newValue, change.propertyName, vocabulary, intl);

    // The log records a change whenever the stored value differs in any way, and
    // that is not always something a reader can see: a value object that lost a
    // field with the CH branch reads the same on both sides. "von X auf X" and
    // "von — auf —" are sentences that say nothing; naming the field is the
    // most that is true here.
    if (from === to) {
        return intl.formatMessage({ id: 'app.stream.sentence.part.touched' }, { field });
    }

    if (from === empty) {
        return intl.formatMessage({ id: 'app.stream.sentence.part.set' }, { field, to });
    }

    if (to === empty) {
        return intl.formatMessage({ id: 'app.stream.sentence.part.cleared' }, { field });
    }

    return intl.formatMessage({ id: 'app.stream.sentence.part' }, { field, from, to });
};

/**
 * The whole sentence for one logged edit.
 *
 * A save that touched eight fields gets the first two by name and the rest as a
 * count — the row stays one line, and the full table is still one click away.
 * Christian's "kein Abschneiden" is about the 30-entry cap on the stream, not
 * about the field list inside a single entry.
 */
export const buildChangeSentence = (
    changes: PoolTimelineItemChange[],
    vocabulary: ChangeVocabulary,
    intl: IntlShape
): string | undefined => {
    if (0 === changes.length) {
        return undefined;
    }

    const named = changes.slice(0, NAMED_FIELDS).map(change => changePart(change, vocabulary, intl));
    const rest = changes.length - named.length;

    if (0 === rest) {
        return intl.formatMessage(
            { id: 'app.stream.sentence.changed' },
            { parts: intl.formatList(named, { type: 'conjunction' }) }
        );
    }

    return intl.formatMessage({ id: 'app.stream.sentence.changedMore' }, { parts: named.join(', '), count: rest });
};
